import React, { useState } from 'react'
import { Card, Row, Col, Avatar, Button, Typography, Space, Divider, Checkbox, Alert, theme, message } from 'antd'
import {
  UserOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined
} from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faArrowRight, faCalendarDay, faEdit } from '@fortawesome/free-solid-svg-icons'
import { bookAppointment } from '../api/searchApi'
import { toNumber } from 'lodash'
import dayjs from 'dayjs'
import { executeBooking, registerBooking } from '../../Bookings/utils/bpm-utils'
import { useSelector } from 'react-redux'
import { createBooking } from '../../Bookings/utils/utils'
import { generateOTP } from '../../lib/utils'

const { Title, Text, Paragraph } = Typography

const AppointmentConfirmation = ({
  doctor,
  selectedSlot,
  selectedReason,
  onBack,
  onConfirmBooking,
  inline = false,
  setCurrentView,
  onClose
}) => {
  const { token } = theme.useToken()
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [bookingDetails, setBookingDetails] = useState(null)
  const authState = useSelector((state) => state.auth)
  const formatDate = (date) => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ]

    const day = dayNames[date.getDay()]
    const dayNum = date.getDate()
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()

    return `${day}, ${dayNum} ${month} ${year}`
  }

  const getDoctorName = () => {
    if (!doctor) return ''
    return `${doctor.firstName} ${doctor.lastName}`.trim()
  }

  const handleConfirmBooking = async () => {
    let processRecord = null
    const processUser = `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`
    const bpmKey = 'patient_journey'
    if (!agreedToTerms) return

    setLoading(true)
    try {
      const firstStep = doctor.clinicRecord.bpmConfig?.[0]?.key || 'check_in'

      processRecord = await registerBooking(bpmKey, processUser)

      processRecord = await executeBooking(
        bpmKey,
        processRecord.recordId,
        'submit',
        processUser,
        processRecord.key,
        firstStep
      )

      // Normalize the date to 02:00:00
      const normalizedDate = dayjs(selectedSlot.date).hour(2).minute(0).second(0).millisecond(0).toDate()
      const otp = generateOTP(6)
      const newBooking = await createBooking({
        userRef: authState.agiliteUser._id,
        otp,
        status: processRecord.key,
        clinicRecord: doctor.clinicRecord,
        medicalProfRef: doctor._id,
        bookingDate: normalizedDate,
        reason: selectedReason,
        chiefComplaint: selectedReason,
        processRef: processRecord.recordId,
        clinicRef: doctor.clinicRecord._id,
        startTime: selectedSlot.time,
        endTime: dayjs(selectedSlot.time, 'HH:mm').add(15, 'minutes').format('HH:mm')
      })

      // Store booking details for success screen
      setBookingDetails({
        ...newBooking,
        doctorName: getDoctorName(),
        date: normalizedDate,
        time: selectedSlot.time,
        reason: selectedReason,
        location: doctor.clinicRecord?.name,
        address: doctor.clinicRecord?.address || doctor.clinicRecord?.physicalAddress
      })
      setShowSuccess(true)
    } catch (error) {
      console.error('Error confirming booking:', error)
      message.error('Failed to confirm booking. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!doctor || !selectedSlot || !selectedReason) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Text>Missing booking information</Text>
      </div>
    )
  }

  // Success Screen Component
  const SuccessScreen = () => (
    <div
      style={{
        background: inline ? 'rgba(255, 255, 255, 0.95)' : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: inline ? '16px' : '0',
        overflow: 'hidden',
        minHeight: inline ? 'auto' : '100vh',
        padding: inline ? '0' : '20px'
      }}
    >
      <div style={{ maxWidth: inline ? '100%' : '600px', margin: '0 auto' }}>
        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden'
          }}
        >
          {/* Success Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              padding: '40px 32px',
              textAlign: 'center',
              color: 'white'
            }}
          >
            <div
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                animation: 'pulse 2s ease-in-out infinite'
              }}
            >
              <CheckCircleOutlined style={{ fontSize: '48px', color: 'white' }} />
            </div>
            <Title level={2} style={{ color: 'white', margin: '0 0 12px 0', fontWeight: '600' }}>
              Appointment Confirmed!
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '18px' }}>
              Your appointment has been successfully booked
            </Text>
          </div>

          {/* Appointment Summary */}
          <div style={{ padding: '32px' }}>
            <Card
              style={{
                marginBottom: '24px',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                border: '1px solid rgba(59, 130, 246, 0.1)'
              }}
            >
              <Title level={4} style={{ marginBottom: '20px', color: '#1f2937' }}>
                <CalendarOutlined style={{ marginRight: '8px', color: '#3b82f6' }} />
                Appointment Details
              </Title>

              <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                <div>
                  <Text strong style={{ color: '#6b7280', fontSize: '14px' }}>
                    Doctor
                  </Text>
                  <div style={{ fontSize: '16px', color: '#1f2937', marginTop: '4px' }}>
                    {bookingDetails?.doctorName}
                  </div>
                </div>

                <div>
                  <Text strong style={{ color: '#6b7280', fontSize: '14px' }}>
                    Date & Time
                  </Text>
                  <div style={{ fontSize: '16px', color: '#1f2937', marginTop: '4px' }}>
                    {bookingDetails && formatDate(bookingDetails.date)} at {bookingDetails?.time}
                  </div>
                </div>

                <div>
                  <Text strong style={{ color: '#6b7280', fontSize: '14px' }}>
                    Location
                  </Text>
                  <div style={{ fontSize: '16px', color: '#1f2937', marginTop: '4px' }}>
                    {bookingDetails?.location}
                    {bookingDetails?.address && (
                      <Text style={{ display: 'block', fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                        {bookingDetails.address}
                      </Text>
                    )}
                  </div>
                </div>

                <div>
                  <Text strong style={{ color: '#6b7280', fontSize: '14px' }}>
                    Reason for Visit
                  </Text>
                  <div style={{ fontSize: '16px', color: '#1f2937', marginTop: '4px' }}>{bookingDetails?.reason}</div>
                </div>

                {bookingDetails?._id && (
                  <div>
                    <Text strong style={{ color: '#6b7280', fontSize: '14px' }}>
                      Booking Reference
                    </Text>
                    <div style={{ fontSize: '16px', color: '#1f2937', marginTop: '4px', fontFamily: 'monospace' }}>
                      {bookingDetails._id}
                    </div>
                  </div>
                )}
              </Space>
            </Card>

            {/* Reminders */}
            <Alert
              type='info'
              showIcon
              message='Important Reminders'
              description={
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Please arrive 15 minutes before your appointment</li>
                  <li>Bring your ID and medical insurance card</li>
                  <li>You will receive a confirmation email shortly</li>
                </ul>
              }
              style={{ marginBottom: '24px' }}
            />

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Button
                type='primary'
                size='large'
                onClick={onClose}
                style={{
                  minWidth: '200px',
                  height: '48px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </div>
  )

  // Show success screen if booking was successful
  if (showSuccess) {
    return <SuccessScreen />
  }

  // Inline version - more compact layout with modern design
  if (inline) {
    return (
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          overflow: 'hidden',
          border: 'none',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          backdropFilter: 'blur(10px)',
          height: '100%'
        }}
      >
        {/* Header with gradient */}
        <div
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            padding: '32px 24px',
            textAlign: 'center',
            color: 'white'
          }}
        >
          {/* Modern checkmark with animation */}
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 8px 32px rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <CheckCircleOutlined
              style={{
                fontSize: '36px',
                color: 'white',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
              }}
            />
          </div>

          <Title
            level={3}
            style={{
              color: 'white',
              marginBottom: '8px',
              fontWeight: '600',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            Confirm Your Appointment
          </Title>
          <Text
            style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '16px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
            }}
          >
            Please review your appointment details and agree to our terms
          </Text>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Appointment Details with modern card */}
          <div style={{ marginBottom: '24px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CalendarOutlined style={{ color: 'white', fontSize: '18px' }} />
              </div>
              <Title
                level={4}
                style={{
                  margin: 0,
                  color: '#1f2937',
                  fontWeight: '600'
                }}
              >
                Appointment Details
              </Title>
            </div>

            <div
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Space direction='vertical' size='small'>
                    <Text strong style={{ color: '#374151', fontSize: '15px' }}>
                      Date:
                    </Text>
                    <Text style={{ fontSize: '14px', color: '#6b7280' }}>{formatDate(selectedSlot.date)}</Text>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction='vertical' size='small'>
                    <Text strong style={{ color: '#374151', fontSize: '15px' }}>
                      Time:
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                        <ClockCircleOutlined style={{ marginRight: '4px', color: '#3b82f6' }} />
                        {selectedSlot.time}
                      </Text>
                      <Button
                        size='small'
                        type='link'
                        onClick={onBack}
                        style={{
                          padding: '0 6px',
                          height: 'auto',
                          fontSize: '12px',
                          color: '#3b82f6',
                          fontWeight: '500'
                        }}
                      >
                        Change <FontAwesomeIcon icon={faCalendarDay} />
                      </Button>
                    </div>
                  </Space>
                </Col>
                <Col span={24}>
                  <Space direction='vertical' size='small'>
                    <Text strong style={{ color: '#374151', fontSize: '15px' }}>
                      Reason for Visit:
                    </Text>
                    <Text style={{ fontSize: '14px', color: '#6b7280' }}>{selectedReason}</Text>
                  </Space>
                </Col>
              </Row>
            </div>
          </div>

          {/* Terms and Conditions with modern styling */}
          <div style={{ marginBottom: '24px' }}>
            <Title
              level={4}
              style={{
                marginBottom: '16px',
                color: '#1f2937',
                fontWeight: '600'
              }}
            >
              Terms and Conditions
            </Title>

            <div
              style={{
                background: 'linear-gradient(135deg, #fefefe 0%, #f9fafb 100%)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                maxHeight: '180px',
                overflowY: 'auto',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
              }}
            >
              <ul
                style={{
                  paddingLeft: '20px',
                  marginBottom: '8px',
                  fontSize: '14px',
                  color: '#4b5563',
                  lineHeight: '1.6'
                }}
              >
                <li>Please arrive 15 minutes before your scheduled appointment time</li>
                <li>Bring a valid form of identification and your health insurance card</li>
                <li>If you need to cancel or reschedule, please provide at least 24 hours notice</li>
                <li>Your personal and medical information will be kept confidential</li>
                <li>Payment is due at the time of service unless other arrangements have been made</li>
              </ul>
            </div>

            <div
              style={{
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.1)'
              }}
            >
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={{ fontSize: '14px' }}
              >
                <Text strong style={{ color: '#1f2937', fontSize: '14px' }}>
                  I have read and agree to the terms and conditions above
                </Text>
              </Checkbox>
            </div>
          </div>

          {!agreedToTerms && (
            <Alert
              message='Please agree to the terms and conditions to proceed with booking'
              type='warning'
              showIcon
              style={{
                marginBottom: '16px',
                borderRadius: '8px',
                border: '1px solid #fbbf24'
              }}
            />
          )}

          {/* Action Buttons with gradient styling */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button
              size='large'
              onClick={onBack}
              style={{
                minWidth: '140px',
                height: '48px',
                borderRadius: '8px',
                fontWeight: '500',
                border: '1px solid #d1d5db',
                color: '#374151'
              }}
            >
              Back to Calendar
            </Button>
            <Button
              type='primary'
              size='large'
              loading={loading}
              disabled={!agreedToTerms}
              onClick={handleConfirmBooking}
              style={{
                minWidth: '140px',
                height: '48px',
                borderRadius: '8px',
                fontWeight: '600',
                background: agreedToTerms ? 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)' : '#9ca3af',
                border: 'none',
                boxShadow: agreedToTerms ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (agreedToTerms) {
                  e.target.style.background = 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)'
                  e.target.style.transform = 'translateY(-1px)'
                  e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)'
                }
              }}
              onMouseLeave={(e) => {
                if (agreedToTerms) {
                  e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)'
                  e.target.style.transform = 'translateY(0)'
                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'
                }
              }}
            >
              Confirm Booking
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Full-page version with enhanced modern design
  return (
    <div
      style={{
        padding: '0',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        minHeight: '100vh'
      }}
    >
      {/* Enhanced Back Button */}
      <div style={{ padding: '20px 20px 0 20px', marginBottom: '20px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            border: 'none',
            color: 'white',
            fontWeight: '600',
            height: '44px',
            paddingLeft: '20px',
            paddingRight: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)'
            e.target.style.transform = 'translateY(-1px)'
            e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)'
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)'
            e.target.style.transform = 'translateY(0)'
            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}
        >
          Back to Calendar
        </Button>
      </div>

      <div style={{ padding: '0 20px 20px 20px' }}>
        <Row gutter={[24, 24]} justify='center'>
          <Col xs={24} lg={16} xl={12}>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '16px',
                overflow: 'hidden',
                border: 'none',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                backdropFilter: 'blur(10px)'
              }}
            >
              {/* Enhanced Header */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                  padding: '40px 32px',
                  textAlign: 'center',
                  color: 'white'
                }}
              >
                {/* Modern checkmark with enhanced styling */}
                <div
                  style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    boxShadow: '0 8px 32px rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '3px solid rgba(255, 255, 255, 0.3)'
                  }}
                >
                  <CheckCircleOutlined
                    style={{
                      fontSize: '48px',
                      color: 'white',
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                    }}
                  />
                </div>

                <Title
                  level={2}
                  style={{
                    color: 'white',
                    marginBottom: '12px',
                    fontWeight: '600',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  Confirm Your Appointment
                </Title>
                <Text
                  style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '18px',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  Please review your appointment details and agree to our terms
                </Text>
              </div>

              <div style={{ padding: '32px' }}>
                {/* Enhanced Doctor Information */}
                <div style={{ marginBottom: '32px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '20px'
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <UserOutlined style={{ color: 'white', fontSize: '18px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#1f2937', fontWeight: '600' }}>
                      Doctor Information
                    </Title>
                  </div>

                  <div
                    style={{
                      background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(34, 197, 94, 0.1)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                      <Avatar size={64} src={doctor.image || '/api/placeholder/64/64'} icon={<UserOutlined />} />
                      <div>
                        <Text strong style={{ fontSize: '18px', display: 'block', color: '#1f2937' }}>
                          {getDoctorName()}
                        </Text>
                        <Text style={{ display: 'block', color: '#6b7280', fontSize: '15px' }}>
                          {doctor.specialty || doctor.extraData?.qualification || 'Medical Practitioner'}
                        </Text>
                        <Text style={{ fontSize: '14px', color: '#9ca3af' }}>
                          {doctor.practice || doctor.clinicRecord?.name || 'Medical Practice'}
                        </Text>
                      </div>
                    </div>

                    <Space align='start' style={{ color: '#6b7280' }}>
                      <EnvironmentOutlined style={{ color: '#10b981', marginTop: '2px' }} />
                      <div>
                        <Text style={{ display: 'block', color: '#374151', fontWeight: '500' }}>
                          {doctor.clinicRecord?.name || getDoctorName()}
                        </Text>
                        <Text style={{ display: 'block', fontSize: '14px', color: '#6b7280' }}>
                          {doctor.clinicRecord?.address ||
                            doctor.clinicRecord?.physicalAddress ||
                            'Address not available'}
                        </Text>
                      </div>
                    </Space>
                  </div>
                </div>

                {/* Enhanced Appointment Details */}
                <div style={{ marginBottom: '32px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '20px'
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CalendarOutlined style={{ color: 'white', fontSize: '18px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#1f2937', fontWeight: '600' }}>
                      Appointment Details
                    </Title>
                  </div>

                  <div
                    style={{
                      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                      padding: '24px',
                      borderRadius: '12px',
                      border: '1px solid rgba(59, 130, 246, 0.1)'
                    }}
                  >
                    <Row gutter={[20, 20]}>
                      <Col span={12}>
                        <Space direction='vertical' size='small'>
                          <Text strong style={{ color: '#1f2937', fontSize: '16px' }}>
                            Date:
                          </Text>
                          <Text style={{ fontSize: '16px', color: '#374151' }}>{formatDate(selectedSlot.date)}</Text>
                        </Space>
                      </Col>
                      <Col span={12}>
                        <Space direction='vertical' size='small'>
                          <Text strong style={{ color: '#1f2937', fontSize: '16px' }}>
                            Time:
                          </Text>
                          <Text style={{ fontSize: '16px', color: '#374151' }}>
                            <ClockCircleOutlined style={{ marginRight: '6px', color: '#3b82f6' }} />
                            {selectedSlot.time}
                          </Text>
                        </Space>
                      </Col>
                      <Col span={24}>
                        <Space direction='vertical' size='small'>
                          <Text strong style={{ color: '#1f2937', fontSize: '16px' }}>
                            Reason for Visit:
                          </Text>
                          <Text style={{ fontSize: '16px', color: '#374151' }}>{selectedReason}</Text>
                        </Space>
                      </Col>
                    </Row>
                  </div>
                </div>

                {/* Enhanced Terms and Conditions */}
                <div style={{ marginBottom: '32px' }}>
                  <Title level={4} style={{ marginBottom: '20px', color: '#1f2937', fontWeight: '600' }}>
                    Terms and Conditions
                  </Title>

                  <div
                    style={{
                      background: 'linear-gradient(135deg, #fefefe 0%, #f9fafb 100%)',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                      maxHeight: '240px',
                      overflowY: 'auto',
                      marginBottom: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                    }}
                  >
                    <Paragraph style={{ marginBottom: '12px', color: '#1f2937', fontWeight: '600' }}>
                      Appointment Policy:
                    </Paragraph>
                    <ul style={{ paddingLeft: '20px', marginBottom: '20px', color: '#4b5563', lineHeight: '1.6' }}>
                      <li>Please arrive 15 minutes before your scheduled appointment time</li>
                      <li>Bring a valid form of identification and your health insurance card</li>
                      <li>If you need to cancel or reschedule, please provide at least 24 hours notice</li>
                      <li>Late arrivals may result in shortened consultation time or rescheduling</li>
                    </ul>

                    <Paragraph style={{ marginBottom: '12px', color: '#1f2937', fontWeight: '600' }}>
                      Privacy and Consent:
                    </Paragraph>
                    <ul style={{ paddingLeft: '20px', marginBottom: '20px', color: '#4b5563', lineHeight: '1.6' }}>
                      <li>Your personal and medical information will be kept confidential</li>
                      <li>Information may be shared with other healthcare providers involved in your care</li>
                      <li>You consent to the collection and use of your health information for treatment purposes</li>
                    </ul>

                    <Paragraph style={{ marginBottom: '12px', color: '#1f2937', fontWeight: '600' }}>
                      Payment Terms:
                    </Paragraph>
                    <ul style={{ paddingLeft: '20px', color: '#4b5563', lineHeight: '1.6' }}>
                      <li>Payment is due at the time of service unless other arrangements have been made</li>
                      <li>We accept cash, credit cards, and most insurance plans</li>
                      <li>Please verify your insurance coverage before your appointment</li>
                    </ul>
                  </div>

                  <div
                    style={{
                      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(59, 130, 246, 0.1)'
                    }}
                  >
                    <Checkbox
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      style={{ fontSize: '16px' }}
                    >
                      <Text strong style={{ color: '#1f2937', fontSize: '16px' }}>
                        I have read and agree to the terms and conditions above
                      </Text>
                    </Checkbox>
                  </div>
                </div>

                {!agreedToTerms && (
                  <Alert
                    message='Please agree to the terms and conditions to proceed with booking'
                    type='warning'
                    showIcon
                    style={{
                      marginBottom: '24px',
                      borderRadius: '12px',
                      border: '1px solid #fbbf24'
                    }}
                  />
                )}

                {/* Enhanced Action Buttons */}
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
                  <Button
                    size='large'
                    onClick={onBack}
                    style={{
                      minWidth: '160px',
                      height: '52px',
                      borderRadius: '8px',
                      fontWeight: '500',
                      fontSize: '16px',
                      border: '1px solid #d1d5db',
                      color: '#374151'
                    }}
                  >
                    Back to Calendar
                  </Button>
                  <Button
                    type='primary'
                    size='large'
                    loading={loading}
                    disabled={!agreedToTerms}
                    onClick={handleConfirmBooking}
                    style={{
                      minWidth: '160px',
                      height: '52px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '16px',
                      background: agreedToTerms ? 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)' : '#9ca3af',
                      border: 'none',
                      boxShadow: agreedToTerms ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (agreedToTerms) {
                        e.target.style.background = 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)'
                        e.target.style.transform = 'translateY(-1px)'
                        e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (agreedToTerms) {
                        e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)'
                        e.target.style.transform = 'translateY(0)'
                        e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'
                      }
                    }}
                  >
                    Confirm Booking
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AppointmentConfirmation
